
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CameraView, CameraHandle } from './components/CameraView';
import { InterfaceOverlay } from './components/InterfaceOverlay';
import { GeminiLiveService } from './services/geminiLive';
import { VoiceId, AccentId, VOICE_OPTIONS } from './types';

const App: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const [transcript, setTranscript] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<VoiceId>(VoiceId.Puck);
  const [selectedAccent, setSelectedAccent] = useState<AccentId>(AccentId.Neutral);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Video Recording State
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Ref for direct communication with Camera component
  const cameraRef = useRef<CameraHandle>(null);
  const geminiServiceRef = useRef<GeminiLiveService | null>(null);

  useEffect(() => {
    geminiServiceRef.current = new GeminiLiveService();
    return () => {
      geminiServiceRef.current?.disconnect();
    };
  }, []);

  const handleStart = useCallback(async () => {
    setError(null);
    setRecordedVideo(null); // Clear previous recording
    setShowShareModal(false);
    setIsConnecting(true);

    const voiceData = VOICE_OPTIONS.find(v => v.id === selectedVoice);
    if (!voiceData) {
        setError("Invalid voice selected");
        setIsConnecting(false);
        return;
    }

    try {
      await geminiServiceRef.current?.connect(
        voiceData.apiVoice, // Pass actual API voice (e.g., 'Fenrir')
        voiceData.systemPrompt, // Pass specific personality
        selectedAccent,
        (text) => {
           setCurrentSubtitle(text);
        },
        (completedSentence) => {
           setTranscript(prev => [...prev, completedSentence]);
        },
        (audioBuffer) => {
           if (cameraRef.current) {
               cameraRef.current.pushAudio(audioBuffer);
           }
        },
        (err) => {
          console.error("Stream error:", err);
          setError(err.message || "Connection interrupted.");
          setIsActive(false);
        }
      );
      setIsActive(true);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to start translator");
      setIsActive(false);
    } finally {
      setIsConnecting(false);
    }
  }, [selectedVoice, selectedAccent]);

  const handleStop = useCallback(() => {
    geminiServiceRef.current?.disconnect();
    setIsActive(false);
    setCurrentSubtitle('');
  }, []);

  const handleRecordingComplete = (blob: Blob) => {
    if (blob && blob.size > 0) {
      setRecordedVideo(blob);
      setShowShareModal(true);
    }
  };

  const handleVoiceChange = (voice: VoiceId) => {
    setSelectedVoice(voice);
    if (isActive) handleStop();
  };

  const handleAccentChange = (accent: AccentId) => {
    setSelectedAccent(accent);
    if (isActive) handleStop();
  };

  const handleDownloadTranscript = () => {
    if (transcript.length === 0) return;
    const header = "DOGLISH TRANSLATION LOG\n=======================\n\n";
    const textContent = header + transcript.join('\n\n');
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Doglish_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveVideo = () => {
    if (!recordedVideo) return;
    const url = URL.createObjectURL(recordedVideo);
    const a = document.createElement('a');
    a.href = url;
    // Guess extension based on type
    const ext = recordedVideo.type.includes('mp4') ? 'mp4' : 'webm';
    a.download = `doglish-translation.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShareVideo = async () => {
    if (!recordedVideo) return;
    
    // Attempt to rename/wrap file
    const ext = recordedVideo.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedVideo], `doglish-translation.${ext}`, { type: recordedVideo.type });
    
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Doglish Translation',
          text: 'Check out what my dog just said! https://doglish.app #Doglish'
        });
      } catch (error) {
        console.log('Error sharing', error);
        // Fallback to save if share fails/cancels
        handleSaveVideo();
      }
    } else {
      handleSaveVideo();
    }
  };

  const handleDiscardVideo = () => {
    setRecordedVideo(null);
    setShowShareModal(false);
  };

  return (
    <div className="relative w-full h-full bg-black text-white overflow-hidden">
      <CameraView 
        ref={cameraRef}
        isActive={isActive} 
        currentSubtitle={currentSubtitle}
        onFrame={(canvas) => geminiServiceRef.current?.sendVideoFrame(canvas)}
        onAudioData={(blob) => geminiServiceRef.current?.sendAudioChunk(blob)}
        onRecordingComplete={handleRecordingComplete}
      />
      
      <InterfaceOverlay
        isActive={isActive}
        isConnecting={isConnecting}
        currentSubtitle={currentSubtitle}
        selectedVoice={selectedVoice}
        selectedAccent={selectedAccent}
        hasTranscript={transcript.length > 0}
        error={error}
        showShareModal={showShareModal}
        recordedVideo={recordedVideo}
        onStart={handleStart}
        onStop={handleStop}
        onVoiceChange={handleVoiceChange}
        onAccentChange={handleAccentChange}
        onDownload={handleDownloadTranscript}
        onShareVideo={handleShareVideo}
        onSaveVideo={handleSaveVideo}
        onDiscardVideo={handleDiscardVideo}
      />
    </div>
  );
};

export default App;
